import GeoJSON from "ol/format/GeoJSON";
import { FeatureType } from "@/geoMap/define/FeatureType";
import { IRealTimeTrafficConvert } from "./IRealTimeTrafficConvert";
import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { RealTimeTrafficAttributeView } from "./RealTimeTrafficAttributeView";
import { RealTimeTrafficAttributeValue } from "./RealTimeTrafficAttributeValue";
import { TraVosCodeUtil } from "./util/TraVosCodeUtil";
import { ExportRealTimeValue } from "./ExportRealTimeValue";

/**
 * 対象FeatureType
 */
const FEATURE_TYPE = FeatureType.TraVosCCDHourRealTimeTraffic;
/**
 * 対象レイヤ名
 */
const LAYER_NAME = "t_travospublic_measure_1h_img";
/**
 * 行ヘッダー/APIプロパティ名 項目マッピング
 */
const PROPERTY_MAP = [
  { header: "上り・小型交通量(台/時間)", property: "上り・小型交通量" },
  { header: "上り・大型交通量(台/時間)", property: "上り・大型交通量" },
  {
    header: "上り・小型大型判別不能交通量(台/時間)",
    property: "上り・小型大型判別不能交通量",
  },
  { header: "下り・小型交通量(台/時間)", property: "下り・小型交通量" },
  { header: "下り・大型交通量(台/時間)", property: "下り・大型交通量" },
  {
    header: "下り・小型大型判別不能交通量(台/時間)",
    property: "下り・小型大型判別不能交通量",
  },
];
/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;
/**
 * 画面幅
 */
const VALUE_WIDTH = 100;
/**
 * 画面高さ
 */
const VALUE_HEIGHT = 100;
/**
 * 画面タイトル
 */
const VALUE_TITLE = "交通量データ（最新）";
/**
 * 合致するデータがない場合の文字列
 */
const TEXT_NO_DATA = "-";
/**
 * 何時間後まで取得するか(データによって変わります)
 */
const END_TIME_HOURS = 12;
/**
 * 何分刻みか
 */
const INTERVAL = 60;
/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME = "traf_qa-8-2_yyyyMMdd_HHmmss.geojson";

/**
* 出力CSVファイル名
*/
const VALUE_CSV_FILE_NAME = "traf_qa-8-2_yyyyMMdd_HHmmss.csv";
/**
 * リアルタイムデータ
 * TraVos属性表示画面（計測値1時間）（画像認識型）用属性取得クラス
 */
export class TraVosCCDHourRealTimeTrafficConvert
  implements IRealTimeTrafficConvert
{
  /**
   * 道路種別
   */
  private _roadType: string;
  /**
   * 常時観測手コード
   */
  private _regularObservationCd: string;
  /**
   * 収集時間フラグ
   */
  private _timeCorrectionFlag: string;
  /**
   * 通称名
   */
  private _commonName: string;
  /**
   * GeoJSON
   */
  private _geoJSON: any;
  /**
   * GeoJSON
   */
  private _realTimeAttributeValues: RealTimeTrafficAttributeValue[];
  /**
   * コンストラクタ
   * @param roadType TraVos道路種別
   * @param regularObservationCd 常時観測点コード
   * @param commonName 通称
   * @param timeCorrectionFlag 収集時間フラグ
   */
  constructor(
    roadType: string,
    regularObservationCd: string,
    commonName: string,
    timeCorrectionFlag: string
  ) {
    this._roadType = roadType;
    this._regularObservationCd = regularObservationCd;
    this._commonName = commonName;
    this._timeCorrectionFlag = timeCorrectionFlag;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FEATURE_TYPE;
  }

  /**
   * 画面表示に必要な各種情報を取得する。
   * @param timeCode 日時コード
   */
  async load(timeCode: string): Promise<RealTimeTrafficAttributeView> {
    // 対象日時コードのDate、startTime, 指定した時間前のendTimeを取得（ミリ秒単位）
    // const startTime = new Date(year, monthIndex, day, hours, minutes, minutes);
    const endTime = new Date(timeCode);
    const startTime = new Date(
      endTime.getTime() - END_TIME_HOURS * 60 * 60 * 1000
    );
    // Date型をyyyymmddhhmm形式の文字列に変換
    const getTimeString = (date: Date): string => {
      const timeString =
        date.getFullYear().toString() +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        date.getDate().toString().padStart(2, "0") +
        date.getHours().toString().padStart(2, "0") +
        date.getMinutes().toString().padStart(2, "0");
      return timeString;
    };
    const startTimeString = getTimeString(startTime);
    const endTimeString = getTimeString(endTime);

    const cqlFilter = `"道路種別" = ${this._roadType} AND "時間コード" >= ${startTimeString} AND "時間コード" <= ${endTimeString} AND "常時観測点コード" = ${this._regularObservationCd}`;
    const cqlParameter = new CqlParameter(LAYER_NAME, cqlFilter);
    const WFSGetter = new WFSGetFeatureManager();
    const geoJSON = await WFSGetter.getInfo(URL_API, [cqlParameter]);
    this._geoJSON = geoJSON
    //GeoJSONからfeature配列を読み込み
    const features = new GeoJSON().readFeatures(geoJSON);

    // endTime → startTimeの5分/1時間刻みのTimeCodeの配列を生成(各日時が1桁の場合は0を挿入)
    const timeCodeArray: string[] = [];
    while (endTime >= startTime) {
      const year = startTime.getFullYear();
      const month = (startTime.getMonth() + 1).toString().padStart(2, "0");
      const date = startTime.getDate().toString().padStart(2, "0");
      const hours = startTime.getHours().toString().padStart(2, "0");
      const minutes = startTime.getMinutes().toString().padStart(2, "0");
      timeCodeArray.push(`${year}${month}${date}${hours}${minutes}`);

      startTime.setTime(startTime.getTime() + INTERVAL * 60 * 1000);
    }
    // 取得した時間コード配列を用いて、属性をマッピング
    const realTimeAttributeValues: RealTimeTrafficAttributeValue[] =
      timeCodeArray.map((timeCode) => {
        const year = timeCode.slice(0, 4);
        const month = timeCode.slice(4, 6);
        const day = timeCode.slice(6, 8);
        const hours = timeCode.slice(8, 10);
        const minutes = timeCode.slice(10, 12);

        let isExist = false; //時間コードに対応したデータが存在するか
        let attributeValues: string[] = [];
        features.forEach((feature) => {
          const featureTimeCode = feature.get("時間コード");
          if (timeCode === featureTimeCode.toString()) {
            // 画面項目をAttributeViewクラスに成形
            // 画面項目に基づき値を取得（値がない場合は"-"）
            attributeValues = PROPERTY_MAP.map(
              (obj) => feature.get(obj.property) ?? TEXT_NO_DATA
            );
            isExist = true;
          }
        });
        // データが存在しなかった場合、TEXT_NO_DATAの文字列で埋めた配列を生成
        if (!isExist) {
          attributeValues = new Array<string>(PROPERTY_MAP.length).fill(
            TEXT_NO_DATA
          );
        }
        const realTimeAttributeValue: RealTimeTrafficAttributeValue = {
          headerDate: `${year}/${month}/${day}`,
          headerTime: `${hours}:${minutes}`,
          attributeValues,
        };
        return realTimeAttributeValue;
      });
    const regularObservationCd = TraVosCodeUtil.toCollectTimeCodeViewString(
      this._timeCorrectionFlag
    );
    const title = `${VALUE_TITLE}&#009;&#009;常時観測点：${this._regularObservationCd}（${this._commonName}）&#009;&#009;収集時間フラグ：${regularObservationCd}`;
    const realTimeAttributeRowHeader = PROPERTY_MAP.map((obj) => obj.header);
    const realTimeTrafficAttributeView: RealTimeTrafficAttributeView = {
      width: VALUE_WIDTH,
      height: VALUE_HEIGHT,
      title,
      realTimeAttributeRowHeader,
      realTimeAttributeValues: realTimeAttributeValues,
    };
    this._realTimeAttributeValues = realTimeAttributeValues;
    return realTimeTrafficAttributeView;
  }
  /**
   * ファイル出力用項目取得
   */
   getExportValue(): ExportRealTimeValue {
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");

    return new ExportRealTimeValue(
      feature,
      this._realTimeAttributeValues,
      PROPERTY_MAP.map((obj) => obj.header),
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }
}
